"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebSocketWorkerManager = exports.calculateTimeToWaitForNextTry = void 0;
var types_core_1 = require("@augloop/types-core");
var session_protocol_1 = require("@augloop/session-protocol");
var logging_1 = require("@augloop/logging/dist/src/client/logging");
var utils_1 = require("../utils");
var jsond_1 = require("@augloop/jsond");
var minTimeToWaitBeforeNextReconnectTryInMs = 1000;
// Exported only for unit testing purposes
var calculateTimeToWaitForNextTry = function (attempt, lastAttemptTimeMs) {
    var result = 0;
    var timeSinceLastConnectMs = utils_1.Utils.getCurrentTimeMs() ? utils_1.Utils.getCurrentTimeMs() - lastAttemptTimeMs : 0;
    var timeoutConstantsBasedOnAttempt = [1000, 2000, 5000, 10000, 60000];
    result = timeoutConstantsBasedOnAttempt[Math.max(0, Math.min(timeoutConstantsBasedOnAttempt.length - 1, attempt - 1))];
    result = Math.max(result - timeSinceLastConnectMs, minTimeToWaitBeforeNextReconnectTryInMs);
    return result;
};
exports.calculateTimeToWaitForNextTry = calculateTimeToWaitForNextTry;
/**
 * Managing websocket workers (websocket connections) in order to create stable connection with successfully initialized session on server
 */
var WebSocketWorkerManager = /** @class */ (function () {
    function WebSocketWorkerManager(globalUrl, workerFactory, sessionInitializer, ingress, onClose) {
        this.logCountLimiter = new utils_1.LogCountLimiter(WebSocketWorkerManager.className);
        this.isWorkerReady = false;
        this.permanentlyClosed = false;
        // Retrying related data
        this.currentReconnectAttempt = 0;
        this.lastInitializationAttemptTimeMs = 0;
        this.alreadyLoggedReconnectAttempt = false;
        this.globalUrl = globalUrl;
        this.workerFactory = workerFactory;
        this.sessionInitializer = sessionInitializer;
        this.ingress = ingress;
        this.onClose = onClose;
    }
    WebSocketWorkerManager.prototype.init = function (extensionConfigs, customInitPromise, initialAttempt) {
        var _this = this;
        if (initialAttempt === void 0) { initialAttempt = false; }
        if (this.permanentlyClosed) {
            return Promise.reject('permanentlyClosed');
        }
        if (this.isWorkerReady || this.pendingInitPromise) {
            if (this.pendingInitPromise) {
                return this.pendingInitPromise;
            }
            else {
                return Promise.resolve();
            }
        }
        this.extensionConfigs = extensionConfigs || this.extensionConfigs;
        var connectionFailingOrSlowLogSetup = function () {
            if (initialAttempt || !_this.alreadyLoggedReconnectAttempt) {
                var timeoutLength_1 = initialAttempt ? WebSocketWorkerManager.initialAttemptTimeout : WebSocketWorkerManager.reconnectAttemptTimeout;
                _this.connTimeout = setTimeout(function () {
                    var connLogOp = new logging_1.Operation({
                        operationName: 'ConnectionFailingOrSlow',
                        dimension0: initialAttempt ? 'Initial' : 'Reconnect',
                        dimension1: timeoutLength_1.toString(),
                        dimension2: _this.currentReconnectAttempt.toString()
                    });
                    logging_1.Log.info(0x2224d0d0 /* tag_8jndq */, logging_1.LogCategory.CoreDefault, connLogOp);
                    _this.alreadyLoggedReconnectAttempt = !initialAttempt;
                    _this.connTimeout = undefined;
                }, timeoutLength_1);
            }
        };
        if (customInitPromise) {
            this.pendingInitPromise = customInitPromise()
                .catch(function (error) {
                logging_1.Log.error(0x2224d0d1 /* tag_8jndr */, logging_1.LogCategory.CoreDefault, "Error occurred during calling the custom init promise: " + error);
            })
                .then(function () {
                // Initialize regardless of the custom init promise failure to make sure we don't
                // block the AL runtime initialization on custom code.
                connectionFailingOrSlowLogSetup();
                _this.initInternal();
            });
        }
        else {
            this.pendingInitPromise = Promise.resolve();
            connectionFailingOrSlowLogSetup();
            this.initInternal();
        }
        return this.pendingInitPromise;
    };
    /**
     * Instruct worker manager to send message to service.
     * It is expected to have ready worker to sent message.
     * @param output object to send to service
     */
    WebSocketWorkerManager.prototype.egress = function (output) {
        var _this = this;
        if (!this.isWorkerReady && this.pendingInitPromise) {
            return this.pendingInitPromise.then(function () {
                _this.egressInternal(output);
            });
        }
        this.egressInternal(output);
        return Promise.resolve();
    };
    /**
     * Instruct worker manager to send message to service.
     * It is expected to have ready worker to sent message.
     * @param bytes bytes to send to service
     */
    WebSocketWorkerManager.prototype.egressBytes = function (bytes) {
        var _this = this;
        if (!this.isWorkerReady && this.pendingInitPromise) {
            return this.pendingInitPromise.then(function () {
                _this.egressBytesInternal(bytes);
            });
        }
        this.egressBytesInternal(bytes);
        return Promise.resolve();
    };
    /**
     * Close current websocket worker
     */
    WebSocketWorkerManager.prototype.close = function (permanentlyStopConnecting) {
        if (this.worker) {
            this.worker.close();
        }
        this.isWorkerReady = false;
        this.worker = null;
        this.permanentlyClosed = permanentlyStopConnecting || this.permanentlyClosed;
    };
    WebSocketWorkerManager.prototype.initInternal = function () {
        var _this = this;
        this.isWorkerReady = false;
        if (!this.permanentlyClosed) {
            if (this.currentReconnectAttempt > 0) {
                setTimeout(function () {
                    _this.tryToConnectAndInitializeSession();
                }, exports.calculateTimeToWaitForNextTry(this.currentReconnectAttempt, this.lastInitializationAttemptTimeMs));
            }
            else {
                this.tryToConnectAndInitializeSession();
            }
        }
    };
    WebSocketWorkerManager.prototype.egressInternal = function (output) {
        if (!this.isWorkerReady && !types_core_1.SchemaObject.matchesTypesFor(output, [session_protocol_1.SessionInitMessage.getTypeName()])) {
            if (!types_core_1.SchemaObject.matchesTypesFor(output, [session_protocol_1.Response.getTypeName()])) {
                logging_1.Log.error(0x2224d0d2 /* tag_8jnds */, logging_1.LogCategory.CoreDefault, new logging_1.Operation({ operationName: 'UnexpectedEgressCall', resultDescription: "isWorkerReady: " + this.isWorkerReady }));
            }
            return;
        }
        var serializedMessage;
        if (types_core_1.SchemaObject.matchesTypesFor(output, [session_protocol_1.MicroSyncMessage.getTypeName()])) {
            this.castBinaryData(output);
            serializedMessage = jsond_1.JSOND.serialize(output);
        }
        else {
            serializedMessage = JSON.stringify(output);
        }
        this.worker.egress(serializedMessage);
    };
    WebSocketWorkerManager.prototype.egressBytesInternal = function (bytes) {
        if (!this.isWorkerReady) {
            logging_1.Log.error(0x2224d0d3 /* tag_8jndt */, logging_1.LogCategory.CoreDefault, new logging_1.Operation({ operationName: 'UnexpectedEgressBytesCall', resultDescription: "isWorkerReady: " + this.isWorkerReady }));
            return;
        }
        this.worker.egress(bytes);
    };
    /**
     * Tries to open connection and initialize session on server.
     * In case of success declare connection ready for using.
     * In case of failure new reconnect attempt will be arranged and retried until success.
     */
    WebSocketWorkerManager.prototype.tryToConnectAndInitializeSession = function () {
        var _this = this;
        this.currentReconnectAttempt++;
        this.lastInitializationAttemptTimeMs = utils_1.Utils.getCurrentTimeMs();
        var connectAndInitSession = function (logOp) {
            if (_this.permanentlyClosed) {
                return;
            }
            // Existence of sliceUrl means that next reconnect should be tried on slice url
            _this.connect(_this.sliceUrl ? _this.sliceUrl : _this.globalUrl);
            _this.sessionInitializer.initSession({
                isTokenRefresh: false,
                isReconnectOnSameSlice: !!_this.sliceUrl,
                extensionConfigs: _this.extensionConfigs,
                onResponse: function (error, response) {
                    _this.logCountLimiter.log(function () {
                        logOp.success = !error;
                        logOp.resultDescription = error ? error.error : '';
                        logOp.resourceId = response ? response.sliceUrl : '';
                        logOp.stop();
                        logging_1.Log.info(0x2224d0d4 /* tag_8jndu */, logging_1.LogCategory.CoreDefault, logOp);
                    });
                    if (error || !_this.worker) {
                        _this.sliceUrl = undefined;
                        _this.close();
                        _this.initInternal();
                    }
                    else if (response.forceReconnect) {
                        _this.close();
                        var logOp_1 = new logging_1.Operation({ operationName: 'ForcedReconnect' }).start();
                        logOp_1.resultSignature = "globalUrl: " + _this.globalUrl + ". sliceUrl: " + _this.sliceUrl;
                        _this.sliceUrl = undefined;
                        setTimeout(function () {
                            connectAndInitSession(logOp_1);
                        }, minTimeToWaitBeforeNextReconnectTryInMs);
                    }
                    else {
                        if (_this.connTimeout !== undefined) {
                            clearTimeout(_this.connTimeout);
                        }
                        _this.sliceUrl = response.sliceUrl;
                        _this.ready();
                    }
                }
            });
        };
        var logOp = new logging_1.Operation({
            operationName: 'TryToConnectAndInitializeSession',
            resultSignature: "Reconnection attempt #" + this.currentReconnectAttempt
        }).start();
        connectAndInitSession(logOp);
    };
    /**
     * Create websocket worker with connection to specified url
     * @param url Url address to make connection with
     */
    WebSocketWorkerManager.prototype.connect = function (url) {
        var _this = this;
        this.worker = this.workerFactory();
        this.worker.init(url, this.ingress, function (error) { _this.onClose(error); _this.close(); });
    };
    /**
     * Declare a current websocket worker ready.
     */
    WebSocketWorkerManager.prototype.ready = function () {
        this.isWorkerReady = true;
        this.pendingInitPromise = undefined;
        this.currentReconnectAttempt = 0;
    };
    /**
     * Cast object's binary fields to Uint8Array so
     * JSOND can serialize it appropriately
     * @param obj Object whose binary data is to be cast to Uint8Array
     */
    WebSocketWorkerManager.prototype.castBinaryData = function (obj) {
        if (!obj) {
            return;
        }
        if (Array.isArray(obj.__binaryMembers__)) {
            for (var _i = 0, _a = obj.__binaryMembers__; _i < _a.length; _i++) {
                var member = _a[_i];
                if (!ArrayBuffer.isView(obj[member])) {
                    obj[member] = new Uint8Array(obj[member]);
                }
            }
        }
        for (var _b = 0, _c = Object.keys(obj); _b < _c.length; _b++) {
            var key = _c[_b];
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                this.castBinaryData(obj[key]);
            }
        }
    };
    // Only for logging purposes
    WebSocketWorkerManager.initialAttemptTimeout = 100000;
    WebSocketWorkerManager.reconnectAttemptTimeout = 20000;
    WebSocketWorkerManager.className = 'WebSocketWorkerManager';
    return WebSocketWorkerManager;
}());
exports.WebSocketWorkerManager = WebSocketWorkerManager;
//# sourceMappingURL=websocket-worker-manager.js.map